import React, {
  useEffect,
  useMemo,
  useContext,
  useState,
  useCallback,
} from "react";
import {
  Alert,
  Box,
  Grid,
  Container,
  Typography,
  Tabs,
  Tab,
  Paper,
  Snackbar,
  IconButton,
} from "@mui/material";
import {
  Close as CloseIcon,
  Settings as SettingsIcon,
  Extension as ExtensionIcon,
  Layers as LayersIcon,
  Storefront as StorefrontIcon,
  Help as HelpIcon,
  Dashboard as DashboardIcon,
  Build as BuildIcon,
  History as HistoryIcon,
  Speed as SpeedIcon,
} from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";

import { BreadcrumbContext } from "../../../../components/Breadcrumb";
import SiteDetailsCard from "../../../../components/SiteDetailsCard";
import SubscriptionDetailsCard from "../../../../components/SubscriptionDetailsCard";
import Loader from "../../../../components/Loader";
import {
  CloudFunctions,
  Firestore,
} from "../../../../components/FirebaseAuth/firebase";
import { SiteMarketPlace } from "./siteComponents/SiteMarketPlace";
import { SiteEnvironmentCards } from "./siteComponents/SiteEnvironmentCards";
import {
  LOCAL_STORAGE_CURRENT_ACCOUNT,
  LOCAL_STORAGE_CURRENT_ORG,
  getLocalStorage,
} from "../../../../utils/localStorage";
import { HelpCenter } from "./siteComponents/HelpCenter";
import { DiscoveredExtensions } from "./siteComponents/DiscoveredExtensions";
import { SiteWorkflows } from "./siteComponents/SiteWorkflows";
import ExtensionMarketplace from "../../../../components/ExtensionMarketplace";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { SectionHeader } from "../../../../components/Common";
import { StatsCard } from "../../../../components/Charts";
import AIAssistant from "../../../../components/AIAssistant";
import { useBreadcrumbs } from "../../../../hooks/useBreadcrumbs";
import SiteTrafficChart from "../../../../components/SiteTrafficChart/SiteTrafficChart";
import ExternalSiteCard from "../../../../components/ExternalSiteCard";

// Tab panel component
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`site-tabpanel-${index}`}
      aria-labelledby={`site-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const SiteDetails = () => {
  const title = "Site Details";
  const params = useParams();
  const history = useNavigate();

  const currentAccount = getLocalStorage(LOCAL_STORAGE_CURRENT_ACCOUNT);
  const currentOrg = getLocalStorage(LOCAL_STORAGE_CURRENT_ORG);

  const [isTemplateSelected, setIsTemplateSelected] = useState(false);
  const [siteData, setSiteData] = useState();
  const [planData, setPlanData] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [progressAction, setProgressAction] = useState();
  const [tabValue, setTabValue] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return parseInt(params.get("tab")) || 0;
  });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const siteTraffic = useMemo(() => {
    const date = new Date();
    const month = date.getMonth() + 1;
    const dateKey = `${month < 10 ? "0" + month : month}-${date.getFullYear()}`;
    if (siteData?.metrics?.[dateKey]?.traffic?.google?.organic?.etv) {
      return new Intl.NumberFormat("en-US", {
        notation: "compact",
        maximumFractionDigits: 0,
      }).format(
        Math.round(
          siteData.metrics?.[dateKey]?.traffic?.google?.organic?.etv ?? 0
        )
      );
    }
    return 0;
  }, [siteData]);
  const handleTabChange = (event, newValue) => {
    // Update the URL without adding to browser history
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set("tab", newValue);
    window.history.replaceState(null, "", `?${searchParams.toString()}`);

    setTabValue(newValue);
  };

  const fetchSiteData = useCallback(async () => {
    console.log("fetchSiteData called with params:", params);
    if (params.siteId) {
      try {
        const siteRef = doc(
          Firestore,
          `/accounts/${params.accountId}/sites`,
          params.siteId
        );
        console.log("Fetching site from path:", siteRef.path);
        const siteSnap = await getDoc(siteRef);

        if (!siteSnap.exists()) {
          console.error("Site not found at path:", siteRef.path);
          setIsLoading(false);
          return;
        }

        const tempSiteData = siteSnap.data();
        console.log("Site data retrieved:", tempSiteData);
        setSiteData(tempSiteData);
        if (tempSiteData?.siteType !== "Manual") {
          if (!tempSiteData?.plan) {
            history(
              `/account/${params.accountId}/billing/plan/${params.siteId}`
            );
          } else {
            if (tempSiteData?.plan?.path) {
              const planRef = doc(Firestore, tempSiteData?.plan?.path);
              const planSnapshot = await getDoc(planRef);

              if (planSnapshot.exists()) {
                setPlanData(planSnapshot.data());
                setIsTemplateSelected(tempSiteData?.isMyTemplate);
              } else {
                console.log("No plan data found!");
              }
            } else {
              console.error("Plan path is not defined in tempSiteData");
            }
          }
        }

        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching site data:", error);
        setIsLoading(false);
      }
    }
  }, [params, history]);

  const handleManageEnvironment = useCallback(
    (envData) => {
      const { accountId, siteId } = params;
      history({
        pathname: `/account/${accountId}/manage-environment/${siteId}`,
        state: { envData },
      });
    },
    [history, params]
  );

  const onCreateEnvironment = useCallback(
    async (envName) => {
      try {
        const siteRef = doc(
          Firestore,
          `/accounts/${params.accountId}/sites`,
          params.siteId
        );
        await updateDoc(siteRef, {
          status: `pending-${envName.toLowerCase()}`,
        });
        await fetchSiteData();

        let deployFunction;
        const data = {
          slug: siteData.slug,
          accountId: params.accountId,
          siteId: params.siteId,
        };

        if (envName === "Test") {
          deployFunction = httpsCallable(
            CloudFunctions,
            "kwallCloud-pantheonSiteTest"
          );
        } else {
          deployFunction = httpsCallable(
            CloudFunctions,
            "kwallCloud-pantheonSiteLive"
          );
        }

        await deployFunction(data);

        setSnackbarMessage(`${envName} environment creation initiated`);
        setSnackbarOpen(true);
      } catch (error) {
        console.error(`Error creating ${envName} environment:`, error);
        setSnackbarMessage(`Error creating ${envName} environment`);
        setSnackbarOpen(true);
      }
    },
    [siteData, params, fetchSiteData]
  );

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleClose = useCallback(() => {
    setProgressAction();
  }, []);

  useEffect(() => {
    fetchSiteData();
  }, [fetchSiteData]);

  // Use breadcrumb service for navigation
  useBreadcrumbs({
    organizationId: currentOrg?.id,
    accountId: params.accountId,
    siteId: params.siteId,
    siteName: siteData?.siteName,
    collectionName: currentAccount?.name,
    accountName: currentAccount?.name
  });

  // Process lighthouse chart data
  const chartData = useMemo(() => {
    if (!siteData) return [];

    const webCheckResult =
      siteData["web_check_result_dev"] ||
      siteData["web_check_result_external"] ||
      {};

    // Define category names mapping to ensure consistent labels
    const categoryNames = {
      performance: "Performance",
      accessibility: "Accessibility", 
      "best-practices": "Best Practices",
      seo: "SEO"
    };

    const categories = [];
    if (webCheckResult?.categories) {
      ["performance", "accessibility", "best-practices", "seo"].forEach(
        (key) => {
          const category = webCheckResult.categories[key];
          categories.push({
            id: key,
            name: category?.name || categoryNames[key],
            score: Math.round((category?.score || 0) * 100),
          });
        }
      );
    }
    return categories;
  }, [siteData]);

  const renderEnvironments = () => (
    <Box>
      <SectionHeader
        title={!isTemplateSelected ? "Environments" : "Environment"}
      />
      <SiteEnvironmentCards
       accountId={params.accountId}
        status={siteData.status}
        slug={siteData.slug}
        pantheonSiteId={siteData.pantheonSiteId}
        onCreateEnvironment={onCreateEnvironment}
        handleManageEnvironment={handleManageEnvironment}
        setProgressAction={setProgressAction}
      />
    </Box>
  );

  const renderExtensions = () => (
    <Box>
      <SectionHeader
        title="Extensions"
        subtitle="Discover and manage powerful automation extensions for your site"
      />
      <ExtensionMarketplace siteData={siteData} />
    </Box>
  );

  const renderMarketplace = () => (
    <Box>
      <SectionHeader
        title="Marketplace"
        subtitle="Browse available apps and extensions for your site"
      />
      <SiteMarketPlace />
    </Box>
  );

  const renderSubscription = () => (
    <Box>
      <SectionHeader
        title="Subscription Details"
        subtitle="Current plan and billing information"
      />
      <SubscriptionDetailsCard planData={planData} />
    </Box>
  );

  const renderTechStack = () => (
    <Box>
      <SectionHeader
        title="Tech Stack"
        subtitle="Discovered technologies and extensions on your site"
      />
      <DiscoveredExtensions siteData={siteData} fetchSiteData={fetchSiteData} />
    </Box>
  );

  const renderWorkflows = () => (
    <Box>
      <SectionHeader
        title="Activity & Workflows"
        subtitle="Site activities, extension logs, and automation workflows"
      />
      <SiteWorkflows siteData={siteData} />
    </Box>
  );

  const renderWebCheck = () => (
    <Box>
      <SectionHeader
        title="Web Check"
        subtitle="Performance analysis and security scanning for your site"
      />
      <ExternalSiteCard />
    </Box>
  );

  const renderHelp = () => (
    <Box>
      <SectionHeader
        title="Help Center"
        subtitle="Resources and support for your site"
      />
      <HelpCenter />
    </Box>
  );

  return (
    <Container maxWidth="xl" sx={{ mt: 3 }}>
      {!!progressAction && (
        <Alert
          severity={
            progressAction.status === "error"
              ? "error"
              : progressAction.status === "info"
              ? "info"
              : "success"
          }
          onClose={handleClose}
          sx={{
            mb: 2,
            display: "flex",
            alignItems: "center",
          }}
        >
          {progressAction.action}
        </Alert>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        action={
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={handleSnackbarClose}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />

      {isLoading ? (
        <Loader> Loading site details...</Loader>
      ) : (
        <Box sx={{ flexGrow: 1 }}>
          {/* Site Details Card */}
          <Box sx={{ mb: 4 }}>
            {siteData && (
              <SiteDetailsCard
                siteData={siteData}
                setIsDeleting={setIsLoading}
              />
            )}
          </Box>

          {/* AI Assistant Section */}
          {siteData && (
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12}>
                <AIAssistant
                  contextType="site"
                  contextData={siteData}
                  accountId={params.accountId}
                  siteId={params.siteId}
                  title="Site AI Assistant"
                  maxHeight={400}
                />
              </Grid>
            </Grid>
          )}

          {chartData.length > 0 && (
            <Grid container spacing={3} sx={{ mb: 4 }}>
              {/* Performance Metrics */}
              <Grid item xs={12} sx={{ mb: -4, mt: 0 }}>
                <SectionHeader title="Core Web Vitals" />
              </Grid>
              {chartData.map((category) => (
                <Grid key={category.id} item xs={12} sm={6} md={3}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      border: "1px solid rgba(0, 0, 0, 0.05)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="h6"
                      color="text.primary"
                      fontWeight="600"
                      textAlign="center"
                      sx={{ mb: 2 }}
                    >
                      {category.name}
                    </Typography>
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: "50%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        bgcolor:
                          category.score >= 90
                            ? "success.light"
                            : category.score >= 70
                            ? "warning.light"
                            : "error.light",
                        mb: 1,
                      }}
                    >
                      <Typography variant="h5" fontWeight="bold">
                        {category.score}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {category.score >= 90
                        ? "Excellent"
                        : category.score >= 70
                        ? "Good"
                        : "Needs Improvement"}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}

          {/* Traffic Analytics */}
          {siteData?.url && siteData.url.length > 0 && (
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12}>
                <SiteTrafficChart 
                  siteId={params.siteId} 
                  accountId={params.accountId} 
                  showSummary={true} 
                />
              </Grid>
            </Grid>
          )}

          {siteData && (!siteData?.url || (siteData?.url && siteData.url.length === 0)) && (
            <Alert severity="info" sx={{ mb: 4 }}>
              Please add your site URL to view traffic statistics and other
              metrics.
            </Alert>
          )}
          {siteData?.url &&
            siteData.url.length > 0 &&
            (siteData?.status ?? "") !== "deleted" && (
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={4}>
                  <StatsCard
                    value={siteTraffic || 0}
                    label="Estimated Site Traffic for next 30 days"
                    icon={<DashboardIcon />}
                    subtitle="This is the estimated traffic for next 30 days, actual traffic may vary"
                    color="warning"
                  />
                </Grid>
              </Grid>
            )}
          {/* Tabs Navigation */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              border: "1px solid rgba(0, 0, 0, 0.05)",
              mb: 3,
            }}
          >
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              aria-label="site details tabs"
              sx={{
                px: 2,
                "& .MuiTab-root": {
                  minHeight: { xs: "56px", sm: "64px" },
                  textTransform: "none",
                  fontWeight: 500,
                  py: 1,
                  px: { xs: 1, sm: 2 },
                },
                "& .MuiTabs-scrollButtons": {
                  color: "primary.main",
                },
              }}
            >
              {siteData && siteData.siteType !== "Manual" && (
                <Tab
                  icon={<LayersIcon />}
                  iconPosition="start"
                  label="Environments"
                  id="site-tab-1"
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    "& .MuiSvgIcon-root": {
                      mr: { xs: 0, sm: 1 },
                      mb: { xs: 0.5, sm: 0 },
                    },
                  }}
                />
              )}

              {siteData && siteData.siteType === "Manual" && (
                <Tab
                  icon={<SpeedIcon />}
                  iconPosition="start"
                  label="Web Check"
                  id="site-tab-webcheck"
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    "& .MuiSvgIcon-root": {
                      mr: { xs: 0, sm: 1 },
                      mb: { xs: 0.5, sm: 0 },
                    },
                  }}
                />
              )}

              <Tab
                icon={<ExtensionIcon />}
                iconPosition="start"
                label="Extensions"
                id="site-tab-2"
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  "& .MuiSvgIcon-root": {
                    mr: { xs: 0, sm: 1 },
                    mb: { xs: 0.5, sm: 0 },
                  },
                }}
              />

              <Tab
                icon={<BuildIcon />}
                iconPosition="start"
                label="Tech Stack"
                id="site-tab-3"
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  "& .MuiSvgIcon-root": {
                    mr: { xs: 0, sm: 1 },
                    mb: { xs: 0.5, sm: 0 },
                  },
                }}
              />

              <Tab
                icon={<StorefrontIcon />}
                iconPosition="start"
                label="Marketplace"
                id="site-tab-4"
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  "& .MuiSvgIcon-root": {
                    mr: { xs: 0, sm: 1 },
                    mb: { xs: 0.5, sm: 0 },
                  },
                }}
              />

              <Tab
                icon={<HistoryIcon />}
                iconPosition="start"
                label="Workflows"
                id="site-tab-5"
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  "& .MuiSvgIcon-root": {
                    mr: { xs: 0, sm: 1 },
                    mb: { xs: 0.5, sm: 0 },
                  },
                }}
              />

              {siteData && siteData.siteType !== "Manual" && (
                <Tab
                  icon={<SettingsIcon />}
                  iconPosition="start"
                  label="Subscription"
                  id="site-tab-6"
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    "& .MuiSvgIcon-root": {
                      mr: { xs: 0, sm: 1 },
                      mb: { xs: 0.5, sm: 0 },
                    },
                  }}
                />
              )}

              <Tab
                icon={<HelpIcon />}
                iconPosition="start"
                label="Help"
                id="site-tab-7"
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  "& .MuiSvgIcon-root": {
                    mr: { xs: 0, sm: 1 },
                    mb: { xs: 0.5, sm: 0 },
                  },
                }}
              />
            </Tabs>
          </Paper>

          {/* Tab Content */}
          {siteData && siteData.siteType !== "Manual" && (
            <TabPanel value={tabValue} index={0}>
              {renderEnvironments()}
            </TabPanel>
          )}

          {siteData && siteData.siteType === "Manual" && (
            <TabPanel value={tabValue} index={0}>
              {renderWebCheck()}
            </TabPanel>
          )}

          <TabPanel
            value={tabValue}
            index={siteData && siteData.siteType !== "Manual" ? 1 : 1}
          >
            {renderExtensions()}
          </TabPanel>

          <TabPanel
            value={tabValue}
            index={siteData && siteData.siteType !== "Manual" ? 2 : 2}
          >
            {renderTechStack()}
          </TabPanel>

          <TabPanel
            value={tabValue}
            index={siteData && siteData.siteType !== "Manual" ? 3 : 3}
          >
            {renderMarketplace()}
          </TabPanel>

          <TabPanel
            value={tabValue}
            index={siteData && siteData.siteType !== "Manual" ? 4 : 4}
          >
            {renderWorkflows()}
          </TabPanel>

          {siteData && siteData.siteType !== "Manual" && (
            <TabPanel
              value={tabValue}
              index={5}
            >
              {renderSubscription()}
            </TabPanel>
          )}

          <TabPanel
            value={tabValue}
            index={siteData && siteData.siteType !== "Manual" ? 6 : 5}
          >
            {renderHelp()}
          </TabPanel>
        </Box>
      )}
    </Container>
  );
};

export default SiteDetails;
